// TrackFiji V3.3.1 — "who's online" heartbeat.
// While a signed-in page is open the browser pings presence_ping() every 30 s (60 s when the tab is hidden).
// "Active" = the person moved the mouse, typed, scrolled or touched the screen in the last 2 minutes and the tab is visible.
// Closing the tab or signing out sends a "left" ping so they drop off straight away; otherwise they time out.
import { useEffect, useState } from "react";
import { supabase } from "./supabase";

export const PING_MS = 30_000;
export const ACTIVE_WINDOW_MS = 2 * 60_000;   // activity within this → "active"
export const ONLINE_MS = 2.5 * 60_000;        // last active within this → Online
export const OPEN_MS = 2.5 * 60_000;          // a heartbeat within this (no activity) → Away

export type Person = {
  user_id: string; full_name: string | null; email: string | null; role: string; org_id: string; org_name: string;
  last_seen: string | null; last_active: string | null; page: string | null; left_at: string | null; is_me: boolean;
};
export type PresenceState = "online" | "away" | "offline";

export function presenceOf(p: Pick<Person, "last_seen" | "last_active" | "left_at">, now = Date.now()): PresenceState {
  if (p.left_at) return "offline";
  const seen = p.last_seen ? now - Date.parse(p.last_seen) : Infinity;
  const active = p.last_active ? now - Date.parse(p.last_active) : Infinity;
  if (seen > OPEN_MS) return "offline";
  return active <= ONLINE_MS ? "online" : "away";
}

const pageName = () => (location.pathname.split("/")[1] || "map").slice(0, 20);

/** Run once for the whole app while signed in. */
export function usePresenceHeartbeat(userId: string | null | undefined) {
  useEffect(() => {
    if (!userId) return;
    let lastInput = Date.now();
    let stopped = false;
    const mark = () => { lastInput = Date.now(); };
    const evs = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"] as const;
    evs.forEach((e) => window.addEventListener(e, mark, { passive: true, capture: true }));

    const ping = (leave = false) => {
      const active = !leave && document.visibilityState === "visible" && Date.now() - lastInput < ACTIVE_WINDOW_MS;
      return supabase.rpc("presence_ping", { p_active: active, p_page: pageName(), p_leave: leave }).then(() => undefined, () => undefined);
    };
    let timer: ReturnType<typeof setTimeout>;
    const loop = () => { if (stopped) return; ping(); timer = setTimeout(loop, document.visibilityState === "visible" ? PING_MS : 2 * PING_MS); };
    loop();
    // coming back to the tab counts as activity straight away
    const vis = () => { if (document.visibilityState === "visible") { mark(); clearTimeout(timer); loop(); } };
    document.addEventListener("visibilitychange", vis);

    // page closed → mark as left (keepalive request survives the unload)
    const leave = async () => {
      const { data } = await supabase.auth.getSession();
      const url = (import.meta.env.VITE_SUPABASE_URL as string) + "/rest/v1/rpc/presence_ping";
      const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      if (!data.session) return;
      try {
        fetch(url, { method: "POST", keepalive: true, headers: { "Content-Type": "application/json", apikey: key, Authorization: `Bearer ${data.session.access_token}` },
          body: JSON.stringify({ p_active: false, p_page: pageName(), p_leave: true }) });
      } catch { /* best effort */ }
    };
    window.addEventListener("pagehide", leave);
    return () => {
      stopped = true;
      clearTimeout(timer);
      evs.forEach((e) => window.removeEventListener(e, mark, { capture: true } as EventListenerOptions));
      document.removeEventListener("visibilitychange", vis);
      window.removeEventListener("pagehide", leave);
    };
  }, [userId]);
}

/** Call before supabase.auth.signOut() so colleagues see you leave immediately. */
export async function presenceSignOut() {
  try { await supabase.rpc("presence_ping", { p_active: false, p_page: pageName(), p_leave: true }); } catch { /* ignore */ }
}

/** People in my company with their presence; refreshes every 30 s. */
export function useCompanyPresence(enabled = true) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!enabled) return;
    let live = true;
    const load = async () => {
      const { data } = await supabase.rpc("company_presence");
      if (live && data) setPeople(data as Person[]);
      setNow(Date.now());
    };
    load();
    const t = setInterval(load, PING_MS);
    const vis = () => document.visibilityState === "visible" && load();
    document.addEventListener("visibilitychange", vis);
    return () => { live = false; clearInterval(t); document.removeEventListener("visibilitychange", vis); };
  }, [enabled]);
  return { people, now };
}
